import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/* Sector icons (line style, no fake UI) */
const sectorIcons = {
  building: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
      <path d="M3 21h18" /><rect x="5" y="3" width="14" height="18" rx="1.5" />
      <path d="M9 21v-4h6v4M9 7h1.5M13.5 7H15M9 11h1.5M13.5 11H15" />
    </svg>
  ),
  palette: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
      <path d="M12 22a10 10 0 1 1 10-10c0 1.7-1.4 3-3.1 3H17a2 2 0 0 0-1.5 3.3A1.9 1.9 0 0 1 14 22z" />
      <circle cx="7.5" cy="10.5" r="1" /><circle cx="12" cy="7.5" r="1" /><circle cx="16" cy="10.5" r="1" />
    </svg>
  ),
  kanban: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
      <rect x="3" y="4" width="5" height="16" rx="1" /><rect x="10" y="4" width="5" height="10" rx="1" /><rect x="17" y="4" width="4" height="13" rx="1" />
    </svg>
  ),
  route: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
      <circle cx="6" cy="19" r="2.2" /><circle cx="18" cy="5" r="2.2" />
      <path d="M8.2 19H14a4 4 0 0 0 0-8h-4a4 4 0 0 1 0-8h5.8" />
    </svg>
  ),
  heart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
      <path d="M2 4h6a3 3 0 0 1 3 3v13a2.5 2.5 0 0 0-2.5-2.5H2z" />
      <path d="M22 4h-6a3 3 0 0 0-3 3v13a2.5 2.5 0 0 1 2.5-2.5H22z" />
    </svg>
  ),
  activity: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
};

const ProjectCard = ({ index, category, title, description, tools, gradient, icon, labelStack, client, year }) => {
  const tags = tools.split(',').map((tt) => tt.trim());
  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slatex-200 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
      {/* Category band */}
      <div className={`relative h-40 overflow-hidden bg-gradient-to-br ${gradient}`}>
        <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
        {/* large watermark icon */}
        <div className="pointer-events-none absolute -bottom-7 right-2 text-white/10">
          {React.cloneElement(icon, { className: 'h-32 w-32' })}
        </div>
        <div className="absolute inset-0 flex flex-col justify-between p-6">
          <div className="flex items-start justify-between">
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
              {category}
            </span>
            <span className="text-sm font-bold text-white/70 font-display">
              {String(index).padStart(2, '0')}
            </span>
          </div>
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition-transform duration-300 group-hover:scale-105">
            {icon}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-6 lg:p-7">
        <h3 className="text-xl font-bold text-ink font-display">{title}</h3>
        {client && (
          <span className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            Client · {client}{year ? ` · ${year}` : ''}
          </span>
        )}
        <p className="mt-3 text-sm leading-relaxed text-slatex-600">{description}</p>
        <div className="mt-6 border-t border-slatex-100 pt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slatex-400">{labelStack}</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag} className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Products = () => {
  const { t } = useTranslation();

  const projects = [
    { key: 'realEstateManagementSystem', gradient: 'from-brand-600 to-ink', icon: sectorIcons.building, client: 'Imobo' },
    { key: 'chuBesancon', gradient: 'from-cyan-500 to-brand-800', icon: sectorIcons.activity, client: 'CHU de Besançon' },
    { key: 'montessoriInscriptions', gradient: 'from-amber-500 to-brand-800', icon: sectorIcons.book, client: 'École Montessori' },
    { key: 'findYourGrafman', gradient: 'from-indigo-500 to-brand-800', icon: sectorIcons.palette },
    { key: 'projectManagementSystem', gradient: 'from-sky-500 to-brand-700', icon: sectorIcons.kanban },
    { key: 'roadShareSystem', gradient: 'from-teal-500 to-brand-800', icon: sectorIcons.route },
    { key: 'unitedCo', gradient: 'from-rose-500 to-brand-800', icon: sectorIcons.heart },
  ];

  return (
    <div className="container-x w-full" id="portfolio">
      <div className="text-center max-w-2xl mx-auto" data-aos="fade-up">
        <span className="eyebrow justify-center">{t('ui.eyebrowWork')}</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-ink">{t('product.title')}</h2>
        <p className="mt-4 text-slatex-600">{t('product.subtitle')}</p>
      </div>

      <div className="mt-14 flex flex-wrap justify-center gap-7" data-aos="fade-up" data-aos-delay="150">
        {projects.map((p, i) => (
          <div key={p.key} className="w-full md:w-[calc(50%-0.875rem)]">
            <ProjectCard
              index={i + 1}
              category={t(`product.${p.key}.category`)}
              title={t(`product.${p.key}.title`)}
              description={t(`product.${p.key}.description`)}
              tools={t(`product.${p.key}.tools`)}
              labelStack={t('product.labelStack')}
              gradient={p.gradient}
              icon={p.icon}
              client={p.client}
              year={p.year}
            />
          </div>
        ))}
      </div>

      <div className="mt-12 text-center" data-aos="fade-up">
        <Link to="/about" className="btn-secondary group">
          {t('product.viewCareer')}
          <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
        </Link>
      </div>
    </div>
  );
};

export default Products;
